import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, FileText, Save, Loader2, Trash2, Pencil, Eye, Download, Copy, Users,
  CheckCircle, AlertTriangle, Sparkles, ChevronUp, ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import {
  PAGE_WIDE, PageHeader, Workbench, WorkbenchList, WorkbenchDetail, Toolbar, SearchInput,
  DataTable, OverflowMenu, DetailDrawer, Dialog, Field, EmptyState, ErrorState, TableSkeleton,
  ProgressBar, StatusPill, PersonAvatar, useUrlTab, fmtDate, humanise, num, plural,
} from '../../components/ui';
import { missingRequired, missingMessage } from './FormFill';

// Forms: the forms an organisation fills in for each client (the shared NDIS templates plus any it
// builds itself), who has completed what, and the answers themselves.
//
// Clients on the left; on the right either the template register or the chosen client's forms.
// Below `lg` the client list is full width and a client opens as its own screen with a Back button
// (Workbench). What is open lives in the address (?open=templates or ?open=<client id>), so Back,
// refresh and a shared link all land in the same place.

// ─── Answer types ─────────────────────────────────────────────────────────────

// What each answer type is called on screen.
const TYPE_LABELS = {
  text: 'Short answer', textarea: 'Long answer', number: 'Number', date: 'Date', time: 'Time',
  select: 'Dropdown', radio: 'Single choice', checkbox: 'Checkboxes', yesno: 'Yes or no',
  boolean: 'Yes or no', signature: 'Signature', heading: 'Section heading',
};
// The answer types the template builder offers. A question saved with another type keeps it.
const BUILDER_TYPES = ['text', 'textarea', 'number', 'date', 'time', 'select', 'radio', 'yesno', 'signature', 'heading'];
const typeOptionsFor = (type) => {
  const t = type || 'text';
  if (BUILDER_TYPES.includes(t)) return BUILDER_TYPES;
  if (t === 'boolean') return BUILDER_TYPES.map((x) => (x === 'yesno' ? 'boolean' : x));
  return [...BUILDER_TYPES, t];
};
const HAS_CHOICES = new Set(['select', 'radio', 'checkbox']);
const FORM_TYPES = ['general', 'assessment', 'consent', 'review', 'incident', 'checklist'];
const isYesNo = (q) => q.type === 'boolean' || q.type === 'yesno';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Answers are keyed by question id; questions saved without one (older builder forms) use their
// label, the same rule the client profile's form filler uses.
const qKey = (q) => (q && (q.id || q.label)) || '';

const questionsOf = (form) => {
  const q = form && form.questions;
  if (Array.isArray(q)) return q;
  if (typeof q === 'string') {
    try { const v = JSON.parse(q); return Array.isArray(v) ? v : []; } catch { return []; }
  }
  return [];
};
const fieldCount = (t) => questionsOf(t).filter((q) => q && q.type !== 'heading').length;
const categoryLabel = (t) => humanise(t.category || t.form_type) || 'General';
const clientName = (c) => `${(c && c.first_name) || ''} ${(c && c.last_name) || ''}`.trim() || 'Client';
const choiceList = (text) => String(text || '').split('\n').map((s) => s.trim()).filter(Boolean);

// Groups questions under their section headings, in order.
function sectionsOf(questions) {
  const out = [];
  let cur = { heading: null, fields: [] };
  for (const q of questions) {
    if (!q) continue;
    if (q.type === 'heading') {
      if (cur.fields.length || cur.heading) out.push(cur);
      cur = { heading: q.label, fields: [] };
    } else {
      cur.fields.push(q);
    }
  }
  if (cur.fields.length || cur.heading) out.push(cur);
  return out;
}

// submitted_at is a bare TIMESTAMP the server reads ~10h early, so date a submission from the epoch
// the API sends alongside it; fall back to submitted_at against an older server.
const instantOf = (s) => {
  if (!s) return null;
  if (s.submitted_epoch != null && Number.isFinite(Number(s.submitted_epoch))) return Number(s.submitted_epoch) * 1000;
  const t = s.submitted_at ? new Date(s.submitted_at).getTime() : NaN;
  return Number.isFinite(t) ? t : null;
};
const fmtInstant = (ms) => (ms == null ? '—' : fmtDate(new Date(ms)));

// Today as YYYY-MM-DD in the viewer's own time zone (never toISOString, which is UTC).
const localYmd = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// A completed form's next review. The server reads it from the form's own review-date answer
// ("Next review date" and similar); a review due today is not yet overdue.
const reviewOf = (s) => {
  if (!s || s.status !== 'completed') return null;
  const d = typeof s.review_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.review_date) ? s.review_date : null;
  return d ? { date: d, overdue: d < localYmd() } : null;
};

// The submission a client's form "is": their latest completed one, else their latest draft.
// Rows arrive newest first; deleted submissions never count.
function bestByClientForm(rows) {
  const map = {};
  for (const s of rows || []) {
    if (!s || !s.client_id || s.status === 'deleted') continue;
    const byForm = map[s.client_id] || (map[s.client_id] = {});
    const cur = byForm[s.form_id];
    if (!cur || (s.status === 'completed' && cur.status !== 'completed')) byForm[s.form_id] = s;
  }
  return map;
}

function completionOf(byForm, templates) {
  let completed = 0; let draft = 0; let overdue = 0;
  for (const t of templates) {
    const s = byForm && byForm[t.id];
    if (!s) continue;
    if (s.status === 'completed') {
      completed += 1;
      if (reviewOf(s)?.overdue) overdue += 1;
    } else {
      draft += 1;
    }
  }
  return { total: templates.length, completed, draft, notStarted: templates.length - completed - draft, overdue };
}

// A drawn signature (from the client profile's form filler) is saved as a PNG data URL.
const IMG_DATA_URL = /^data:image\/(png|jpe?g);base64,[A-Za-z0-9+/=]+$/;
const isImageAnswer = (v) => typeof v === 'string' && IMG_DATA_URL.test(v);

function answerText(q, v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (v == null || v === '') return '';
  if (Array.isArray(v)) return v.join(', ');
  if (q.type === 'date' && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return fmtDate(v);
  return String(v);
}

function typeDescription(q) {
  const label = TYPE_LABELS[q.type] || humanise(q.type) || TYPE_LABELS.text;
  const opts = Array.isArray(q.options) ? q.options.filter(Boolean) : [];
  return HAS_CHOICES.has(q.type) && opts.length ? `${label}: ${opts.join(', ')}` : label;
}

let keySeq = 0;
const newKey = () => { keySeq += 1; return `k${Date.now().toString(36)}${keySeq}`; };
const newQuestionId = () => `q_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

// ─── Hooks ────────────────────────────────────────────────────────────────────

// Dialog and DetailDrawer re-run their focus set-up whenever onClose changes identity, which pulls
// focus out of the field being typed in. This keeps one stable function that calls the latest one.
function useStableHandler(fn) {
  const ref = useRef(fn);
  useLayoutEffect(() => { ref.current = fn; });
  return useCallback((...args) => (ref.current ? ref.current(...args) : undefined), []);
}

// While a long form has unsaved work, closing or reloading the browser tab asks first.
function useUnsavedGuard(dirty) {
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
}

// ─── Printable PDF ────────────────────────────────────────────────────────────
// Opens the browser's print dialog with the form laid out as a document (Save as PDF from there).
// Every answer is escaped: this page is written into a window that shares the app's origin, so a
// typed answer must never be able to run as HTML.

const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

// Accent per category in the printed document only; the screen itself stays neutral.
const CATEGORY_COLOR = {
  assessment: '#2563eb', risk: '#dc2626', emergency: '#ea580c',
  consent: '#16a34a', support_plan: '#7c3aed', restrictive_practice: '#b91c1c',
  mental_health: '#0d9488', service_agreement: '#6d28d9', default: '#4f46e5',
};

const PRINT_CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1e293b; background: #fff; }
  .doc { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .doc + .doc { break-before: page; page-break-before: always; }
  .cover { background: var(--accent); color: #fff; padding: 28px 40px 22px; display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
  .cover-left { flex: 1; }
  .cover-logo { font-size: 13px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; opacity: 0.85; margin-bottom: 10px; }
  .cover-title { font-size: 22px; font-weight: 800; line-height: 1.2; margin-bottom: 6px; }
  .cover-subtitle { font-size: 11px; opacity: 0.8; letter-spacing: 0.01em; }
  .cover-badge { display: inline-block; margin-top: 10px; padding: 4px 12px; border-radius: 20px; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; background: rgba(255,255,255,0.2); border: 1px solid rgba(255,255,255,0.4); }
  .cover-badge.completed { background: rgba(255,255,255,0.9); color: var(--accent); }
  .client-strip { background: var(--accent-light); border-bottom: 2px solid var(--accent-30); padding: 12px 40px; display: flex; align-items: center; gap: 32px; }
  .client-avatar { width: 36px; height: 36px; border-radius: 50%; background: var(--accent); color: #fff; font-size: 14px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .client-info { flex: 1; }
  .client-name { font-size: 14px; font-weight: 800; color: #0f172a; }
  .client-sub { font-size: 10px; color: #64748b; margin-top: 1px; }
  .meta-pill { display: flex; gap: 16px; }
  .meta-item { text-align: center; }
  .meta-item .ml { font-size: 9px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
  .meta-item .mv { font-size: 11px; font-weight: 700; color: #334155; margin-top: 1px; }
  .body { padding: 24px 40px 0; }
  .section { margin-bottom: 20px; page-break-inside: avoid; }
  .section-heading { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid var(--accent); }
  .section-num { width: 20px; height: 20px; border-radius: 50%; background: var(--accent); color: #fff; font-size: 10px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .section-heading span { font-size: 11px; font-weight: 800; color: var(--accent); text-transform: uppercase; letter-spacing: 0.07em; }
  .field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; margin-bottom: 8px; }
  .field { margin-bottom: 8px; }
  .field-label { font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 3px; }
  .field-value { font-size: 11px; color: #1e293b; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 5px; padding: 6px 10px; min-height: 26px; line-height: 1.5; white-space: pre-wrap; word-break: break-word; }
  .field-value.empty { color: #cbd5e1; font-style: italic; }
  .bool-row { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
  .bool-box { display: inline-flex; align-items: center; gap: 4px; padding: 4px 12px; border-radius: 20px; border: 1px solid #e2e8f0; font-size: 10px; font-weight: 600; color: #94a3b8; background: #f8fafc; }
  .bool-yes { background: #dcfce7; border-color: #86efac; color: #166534; }
  .bool-no { background: #fee2e2; border-color: #fca5a5; color: #991b1b; }
  .not-answered { font-size: 10px; color: #cbd5e1; font-style: italic; }
  .sig-field { page-break-inside: avoid; }
  .sig-box { border: 1px solid #e2e8f0; border-radius: 5px; background: #fafafa; padding: 8px 12px 6px; min-height: 70px; display: flex; flex-direction: column; justify-content: space-between; }
  .sig-text { font-size: 16px; font-family: Georgia, serif; color: #1e293b; padding: 4px 0 8px; }
  .sig-img { display: block; max-height: 60px; max-width: 240px; margin: 2px 0 6px; }
  .sig-placeholder { flex: 1; }
  .sig-line-row { display: flex; gap: 32px; margin-top: 4px; }
  .sig-underline { flex: 1; border-top: 1px solid #94a3b8; padding-top: 4px; }
  .sig-underline span { font-size: 9px; color: #94a3b8; }
  .footer { margin-top: 32px; padding: 12px 40px; background: #f8fafc; border-top: 2px solid var(--accent); display: flex; align-items: center; justify-content: space-between; }
  .footer-left { font-size: 9px; color: #94a3b8; line-height: 1.6; }
  .footer-left strong { color: #475569; }
  .footer-right { font-size: 9px; color: #94a3b8; text-align: right; }
  .ndis-badge { display: inline-block; border: 1px solid var(--accent-60); color: var(--accent); font-size: 9px; font-weight: 700; padding: 2px 8px; border-radius: 4px; letter-spacing: 0.05em; margin-bottom: 4px; }
  @media print { body { padding: 0; } .section { page-break-inside: avoid; } .sig-field { page-break-inside: avoid; } }
`;

// One form as a printable section. Several sections can share one document (one per page).
function formDocSection(submission, form, name, companyName) {
  const accent = CATEGORY_COLOR[form.category] || CATEGORY_COLOR.default;
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
  const isCompleted = submission.status === 'completed';
  const responses = submission.responses || {};
  const initials = String(name || '').split(/\s+/).filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  const renderField = (q) => {
    const val = responses[qKey(q)];
    const label = esc(q.label);
    if (q.type === 'signature') {
      const mark = isImageAnswer(val) ? `<img class="sig-img" src="${val}" alt="Signature"/>`
        : (val ? `<div class="sig-text">${esc(answerText(q, val))}</div>` : '<div class="sig-placeholder"></div>');
      return `<div class="field sig-field"><div class="field-label">${label}</div><div class="sig-box">${mark}`
        + '<div class="sig-line-row"><div class="sig-underline"><span>Signature</span></div><div class="sig-underline"><span>Date</span></div></div></div></div>';
    }
    if (isYesNo(q)) {
      const answered = val === true || val === false;
      return `<div class="field"><div class="field-label">${label}</div><div class="bool-row">`
        + `<span class="bool-box ${val === true ? 'bool-yes' : ''}">✓ Yes</span><span class="bool-box ${val === false ? 'bool-no' : ''}">✗ No</span>`
        + `${answered ? '' : '<span class="not-answered">Not answered</span>'}</div></div>`;
    }
    const text = answerText(q, val);
    return `<div class="field"><div class="field-label">${label}</div>`
      + `<div class="field-value${text ? '' : ' empty'}">${text ? esc(text).replace(/\n/g, '<br/>') : '—'}</div></div>`;
  };

  // Short answers sit two to a row; long answers, yes/no and signatures take the full width.
  const renderSection = (section, idx) => {
    const isLong = (q) => q.type === 'textarea' || q.type === 'signature' || isYesNo(q);
    const shortFields = section.fields.filter((q) => !isLong(q));
    const longFields = section.fields.filter(isLong);
    const body = shortFields.length >= 2
      ? `<div class="field-grid">${shortFields.map(renderField).join('')}</div>${longFields.map(renderField).join('')}`
      : section.fields.map(renderField).join('');
    const heading = section.heading
      ? `<div class="section-heading"><div class="section-num">${idx + 1}</div><span>${esc(section.heading)}</span></div>` : '';
    return `<div class="section">${heading}${body}</div>`;
  };

  return `<section class="doc" style="--accent:${accent};--accent-light:${accent}18;--accent-30:${accent}30;--accent-60:${accent}60">
  <div class="cover"><div class="cover-left">
    <div class="cover-logo">${esc(companyName)}</div>
    <div class="cover-title">${esc(form.name)}</div>
    <div class="cover-subtitle">NDIS Provider Documentation · Confidential</div>
    <div class="cover-badge ${isCompleted ? 'completed' : ''}">${isCompleted ? 'Completed' : 'Draft'}</div>
  </div></div>
  <div class="client-strip">
    <div class="client-avatar">${esc(initials)}</div>
    <div class="client-info">
      <div class="client-name">${esc(name)}</div>
      <div class="client-sub">Participant — ${esc(form.name)}</div>
    </div>
    <div class="meta-pill"><div class="meta-item"><div class="ml">Status</div><div class="mv">${isCompleted ? 'Completed' : 'Draft'}</div></div></div>
  </div>
  <div class="body">${sectionsOf(questionsOf(form)).map(renderSection).join('')}</div>
  <div class="footer">
    <div class="footer-left">
      <div class="ndis-badge">NDIS REGISTERED PROVIDER</div><br/>
      <strong>${esc(companyName)}</strong> · NDIS Provider Documentation<br/>
      This document is confidential and intended for authorised recipients only.
    </div>
    <div class="footer-right">
      Generated ${esc(dateStr)} at ${esc(timeStr)}<br/>
      Ref: ${esc(String(submission.id || '').slice(0, 8).toUpperCase())}<br/>
      Rostery Provider Management System
    </div>
  </div>
</section>`;
}

// items: [{ submission, form }]. All of them open in ONE window, one form per page, because a
// browser lets a click open one window: separate windows for each form were blocked after the first.
function printForms(items, name, companyName) {
  if (!items.length) return;
  const title = items.length === 1 ? `${items[0].form.name} — ${name}` : `${name} — completed forms`;
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><title>${esc(title)}</title>`
    + `<style>${PRINT_CSS}</style></head><body>${items.map(({ submission, form }) => formDocSection(submission, form, name, companyName)).join('')}</body></html>`;
  const w = window.open('', '_blank');
  if (!w) { toast.error('Pop-up blocked. Allow pop-ups for this site, then try again.'); return; }
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 600);
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const ROW_BTN = 'w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-100 transition-colors duration-150 '
  + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400';
const CHOICE_BTN = 'inline-flex h-9 items-center justify-center rounded-lg border px-4 text-sm font-medium transition-colors duration-150 '
  + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1';
const CHOICE_ON = 'border-primary-600 bg-primary-600 text-white';
const CHOICE_OFF = 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50';
const LEGEND = 'mb-1.5 text-[13px] font-medium text-slate-700';
const ICON_BASE = 'inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors duration-150 '
  + 'disabled:opacity-40 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400';
const ICON_BTN = `${ICON_BASE} text-slate-500 hover:bg-slate-200/70 hover:text-slate-800`;
const ICON_BTN_DANGER = `${ICON_BASE} text-slate-500 hover:bg-rose-50 hover:text-rose-600`;

// ─── Fill a form ──────────────────────────────────────────────────────────────

function FormFillDialog({ template, client, submission: initialSub, onClose, onSaved }) {
  const confirm = useConfirm();
  const [responses, setResponses] = useState(() => ({ ...((initialSub && initialSub.responses) || {}) }));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(null); // 'draft' | 'completed' while a save is running
  const [aiLoading, setAiLoading] = useState(false);
  const dirtyRef = useRef(false);
  useUnsavedGuard(dirty);

  const markDirty = () => { if (!dirtyRef.current) { dirtyRef.current = true; setDirty(true); } };
  const setVal = (key, val) => { setResponses((r) => ({ ...r, [key]: val })); markDirty(); };

  const questions = questionsOf(template);
  const answerable = questions.filter((q) => q.type !== 'heading' && q.type !== 'signature');
  const answered = answerable.filter((q) => {
    const v = responses[qKey(q)];
    return v !== undefined && v !== null && v !== '';
  }).length;
  const progress = answerable.length > 0 ? Math.round((answered / answerable.length) * 100) : 0;
  const name = clientName(client);

  const requestClose = useStableHandler(async () => {
    if (saving) return;
    if (dirtyRef.current) {
      const ok = await confirm('Answers you have not saved will be lost.', {
        title: 'Close without saving?', confirmLabel: 'Close without saving', danger: true,
      });
      if (!ok) return;
    }
    onClose();
  });

  const handleAIPrefill = async () => {
    setAiLoading(true);
    try {
      const res = await api.post('/forms/ai-prefill', { clientId: client.id, formId: template.id });
      const { responses: aiResponses, filledCount } = res.data || {};
      if (aiResponses && Object.keys(aiResponses).length) {
        setResponses((r) => ({ ...r, ...aiResponses }));
        markDirty();
      }
      toast.success(`AI filled in ${plural(filledCount || 0, 'field')}. Review each answer before you save.`);
    } catch {
      toast.error('AI auto-fill failed. Try again, or fill the form in by hand.');
    } finally {
      setAiLoading(false);
    }
  };

  const save = async (statusVal) => {
    if (saving) return;
    const isFinal = statusVal === 'completed';
    // A draft can be saved half done; completing needs every required question answered (the same
    // check as the client profile's form filler).
    if (isFinal) {
      const missing = missingRequired(questions, responses);
      if (missing.length) { toast.error(missingMessage(missing)); return; }
    }
    setSaving(statusVal);
    try {
      if (initialSub?.id) {
        await api.put(`/forms/submissions/${initialSub.id}`, { responses, status: statusVal });
      } else {
        await api.post(`/forms/${template.id}/submit`, {
          clientId: client.id,
          responses,
          status: statusVal,
          title: `${template.name} — ${client.first_name} ${client.last_name}`,
        });
      }
      dirtyRef.current = false;
      toast.success(isFinal ? 'Form completed' : 'Draft saved');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the form. Try again.');
    } finally {
      setSaving(null);
    }
  };

  const renderField = (q, i) => {
    const key = qKey(q);
    const rk = `${key || 'q'}-${i}`;
    const val = responses[key];
    const set = (v) => setVal(key, v);
    const required = !!q.required;
    const star = required ? <span className="ml-0.5 text-rose-500" aria-hidden="true">*</span> : null;
    const hint = q.hint ? <p className="-mt-0.5 mb-2 text-[12px] text-slate-500">{q.hint}</p> : null;

    switch (q.type) {
      case 'heading':
        return (
          <h3 key={rk} className="sm:col-span-2 pt-3 first:pt-0 pb-2 border-b border-slate-200 text-[13.5px] font-semibold text-slate-900">
            {q.label}
          </h3>
        );
      case 'signature':
        return (
          <Field key={rk} className="sm:col-span-2" label={q.label} required={required}
            hint={q.hint || 'By typing your name you acknowledge this as your electronic signature.'}>
            {(id) => (isImageAnswer(val) ? (
              <div className="flex flex-wrap items-center gap-3">
                <img src={val} alt={`Signature: ${q.label}`} className="h-14 max-w-[240px] rounded-lg border border-slate-200 bg-white object-contain" />
                <button type="button" className="btn-secondary btn-sm !h-9" onClick={() => set('')}>Replace with a typed name</button>
              </div>
            ) : (
              <input id={id} className="input-field" autoComplete="off" placeholder="Type full name as signature"
                value={val || ''} onChange={(e) => set(e.target.value)} aria-required={required || undefined} />
            ))}
          </Field>
        );
      case 'boolean':
      case 'yesno':
        return (
          <fieldset key={rk} className="sm:col-span-2 min-w-0">
            <legend className={LEGEND}>{q.label}{star}</legend>
            {hint}
            <div className="flex gap-2">
              <button type="button" aria-pressed={val === true} onClick={() => set(val === true ? undefined : true)}
                className={clsx(CHOICE_BTN, val === true ? CHOICE_ON : CHOICE_OFF)}>Yes</button>
              <button type="button" aria-pressed={val === false} onClick={() => set(val === false ? undefined : false)}
                className={clsx(CHOICE_BTN, val === false ? CHOICE_ON : CHOICE_OFF)}>No</button>
            </div>
          </fieldset>
        );
      case 'radio':
        return (
          <fieldset key={rk} className="sm:col-span-2 min-w-0">
            <legend className={LEGEND}>{q.label}{star}</legend>
            {hint}
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(q.options) ? q.options : []).map((o) => (
                <button key={o} type="button" aria-pressed={val === o} onClick={() => set(o)}
                  className={clsx(CHOICE_BTN, '!px-3', val === o ? CHOICE_ON : CHOICE_OFF)}>{o}</button>
              ))}
            </div>
          </fieldset>
        );
      case 'textarea':
        return (
          <Field key={rk} className="sm:col-span-2" label={q.label} required={required} hint={q.hint}>
            {(id) => (
              <textarea id={id} className="input-field min-h-[88px] resize-y" value={val || ''} placeholder={q.placeholder || ''}
                onChange={(e) => set(e.target.value)} aria-required={required || undefined} />
            )}
          </Field>
        );
      case 'select': {
        const opts = Array.isArray(q.options) ? q.options : [];
        return (
          <Field key={rk} label={q.label} required={required} hint={q.hint}>
            {(id) => (
              <select id={id} className="input-field" value={val || ''} onChange={(e) => set(e.target.value)} aria-required={required || undefined}>
                <option value="">Select…</option>
                {val && !opts.includes(val) && <option value={val}>{val}</option>}
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
          </Field>
        );
      }
      case 'date':
      case 'time':
      case 'number':
        return (
          <Field key={rk} label={q.label} required={required} hint={q.hint}>
            {(id) => (
              <input id={id} type={q.type} className="input-field" value={val == null ? '' : val}
                onChange={(e) => set(e.target.value)} aria-required={required || undefined} />
            )}
          </Field>
        );
      default:
        return (
          <Field key={rk} label={q.label} required={required} hint={q.hint}>
            {(id) => (
              <input id={id} type="text" className="input-field" value={val == null ? '' : String(val)} placeholder={q.placeholder || ''}
                onChange={(e) => set(e.target.value)} aria-required={required || undefined} />
            )}
          </Field>
        );
    }
  };

  return (
    <Dialog open onClose={requestClose} dismissable={false} size="xl" title={template.name} description={`For ${name}`}
      footer={(
        <>
          <span className="mr-auto text-[12.5px] text-slate-500" aria-live="polite">{dirty ? 'Unsaved changes' : ''}</span>
          <button type="button" onClick={requestClose} disabled={!!saving} className="btn-secondary">Cancel</button>
          <button type="button" onClick={() => save('draft')} disabled={!!saving} className="btn-secondary">
            {saving === 'draft' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save draft
          </button>
          <button type="button" onClick={() => save('completed')} disabled={!!saving} className="btn-primary">
            {saving === 'completed' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Complete and submit
          </button>
        </>
      )}>
      <div className="sticky top-0 z-10 -mx-5 sm:-mx-6 mb-4 flex flex-wrap items-end gap-3 border-b border-slate-100 bg-white px-5 sm:px-6 pt-1 pb-3">
        <div className="min-w-[12rem] flex-1">
          <div className="mb-1.5 flex justify-between gap-3 text-[12.5px] text-slate-500">
            <span className="tabular-nums">{answered} of {answerable.length} answered</span>
            <span className="tabular-nums">{progress}%</span>
          </div>
          <ProgressBar pct={progress} tone="emerald" className="!h-1.5" />
        </div>
        <button type="button" onClick={handleAIPrefill} disabled={aiLoading} className="btn-secondary btn-sm !h-9"
          title="Reads the client's profile and fills in the fields it can">
          {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {aiLoading ? 'Filling…' : 'Auto-fill with AI'}
        </button>
      </div>
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
        {questions.map(renderField)}
      </div>
    </Dialog>
  );
}

// ─── View a submission ────────────────────────────────────────────────────────

function ViewSubmissionDialog({ submission, template, client, companyName, onClose, onDeleted }) {
  const confirm = useConfirm();
  const [deleting, setDeleting] = useState(false);
  const close = useStableHandler(onClose);
  const questions = questionsOf(template).length ? questionsOf(template) : questionsOf(submission);
  const responses = submission.responses || {};
  const name = clientName(client);
  const done = submission.status === 'completed';
  const when = instantOf(submission);

  const handleDelete = async () => {
    const ok = await confirm(`${template.name} for ${name} will no longer appear in their forms.`, {
      title: 'Delete this submission?', confirmLabel: 'Delete submission', danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      // Soft delete: the submission is marked deleted, not erased.
      await api.put(`/forms/submissions/${submission.id}`, { responses: submission.responses, status: 'deleted' });
      toast.success('Submission deleted');
      onDeleted();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not delete the submission. Try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onClose={close} size="lg" title={template.name}
      description={`${name} · ${done ? 'Completed' : 'Draft'}${when != null ? ` ${fmtInstant(when)}` : ''}`}
      footer={(
        <>
          <button type="button" onClick={handleDelete} disabled={deleting}
            className="btn-ghost mr-auto text-rose-600 hover:bg-rose-50 hover:text-rose-700">
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete submission
          </button>
          <button type="button" className="btn-secondary"
            onClick={() => printForms([{ submission, form: { ...template, questions } }], name, companyName)}>
            <Download size={14} /> Download PDF
          </button>
          <button type="button" onClick={close} className="btn-secondary">Close</button>
        </>
      )}>
      <div className="space-y-5">
        {sectionsOf(questions).map((s, si) => (
          <section key={si}>
            {s.heading && <h3 className="border-b border-slate-200 pb-2 text-[13.5px] font-semibold text-slate-900">{s.heading}</h3>}
            <dl className="divide-y divide-slate-100">
              {s.fields.map((q, qi) => {
                const val = responses[qKey(q)];
                const text = answerText(q, val);
                return (
                  <div key={`${qKey(q)}-${qi}`} className="grid grid-cols-1 gap-x-4 gap-y-0.5 py-2.5 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                    <dt className="text-[12.5px] text-slate-500">{q.label}</dt>
                    <dd className="whitespace-pre-wrap break-words text-[13.5px] text-slate-900">
                      {isImageAnswer(val) ? (
                        <img src={val} alt={`Signature: ${q.label}`} className="h-14 max-w-[240px] rounded border border-slate-200 bg-white object-contain" />
                      ) : text ? (
                        <>
                          {text}
                          {q.type === 'signature' && <span className="block text-[12px] text-slate-500">Electronic signature</span>}
                        </>
                      ) : (
                        <span className="text-slate-500">{q.type === 'signature' ? 'Not signed' : 'Not answered'}</span>
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </section>
        ))}
      </div>
    </Dialog>
  );
}

// ─── Build or edit a template ─────────────────────────────────────────────────
// POST /forms and PUT /forms/:id; the server applies the same name and label rules.

function TemplateBuilderDialog({ template, onClose, onSaved }) {
  const confirm = useConfirm();
  const isEdit = !!(template && template.id);
  const [name, setName] = useState((template && template.name) || '');
  const [formType, setFormType] = useState((template && template.form_type) || 'general');
  // _k is a key for this screen only; _choices is the editable text of a question's choices.
  // Neither is saved. A new question gets a real id so its answers never collide with another's.
  const [questions, setQuestions] = useState(() => {
    const list = questionsOf(template);
    if (!list.length) return [{ _k: newKey(), id: newQuestionId(), label: '', type: 'text', required: false, _choices: '' }];
    return list.map((q) => ({ ...q, _k: newKey(), _choices: Array.isArray(q.options) ? q.options.join('\n') : '' }));
  });
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const focusKey = useRef(null);
  useUnsavedGuard(dirty);

  const touch = () => { if (!dirtyRef.current) { dirtyRef.current = true; setDirty(true); } };
  const setQ = (i, patch) => { touch(); setQuestions((qs) => qs.map((q, n) => (n === i ? { ...q, ...patch } : q))); };
  const addQ = () => {
    touch();
    const k = newKey();
    focusKey.current = k;
    setQuestions((qs) => [...qs, { _k: k, id: newQuestionId(), label: '', type: 'text', required: false, _choices: '' }]);
  };
  const delQ = (i) => { touch(); setQuestions((qs) => (qs.length === 1 ? qs : qs.filter((_, n) => n !== i))); };
  const moveQ = (i, dir) => {
    touch();
    setQuestions((qs) => {
      const j = i + dir;
      if (j < 0 || j >= qs.length) return qs;
      const next = [...qs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  // A newly added question takes the focus, ready to type.
  useEffect(() => {
    if (!focusKey.current) return;
    const el = document.getElementById(`qb-${focusKey.current}`);
    focusKey.current = null;
    if (el) el.focus();
  }, [questions]);

  // Mirrors the server's rules so the problem is named before a round trip, not after.
  const nameError = !name.trim() ? 'A form name is required.'
    : name.trim().length > 200 ? 'Keep the name to 200 characters or fewer.' : null;
  const problems = questions.map((q, i) => {
    if (!String(q.label || '').trim()) return q.type === 'heading' ? `Section ${i + 1} needs a title.` : `Question ${i + 1} needs a label.`;
    if (HAS_CHOICES.has(q.type) && !choiceList(q._choices).length) return `Question ${i + 1} needs at least one choice.`;
    return null;
  });
  const problemCount = (nameError ? 1 : 0) + problems.filter(Boolean).length;

  const requestClose = useStableHandler(async () => {
    if (saving) return;
    if (dirtyRef.current) {
      const ok = await confirm('Your changes to this template have not been saved.', {
        title: 'Close without saving?', confirmLabel: 'Close without saving', danger: true,
      });
      if (!ok) return;
    }
    onClose();
  });

  const save = async () => {
    if (saving) return;
    setTried(true);
    if (problemCount) return;
    setSaving(true);
    try {
      const clean = questions.map(({ _k, _choices, ...q }) => (HAS_CHOICES.has(q.type) ? { ...q, options: choiceList(_choices) } : q));
      const payload = { name: name.trim(), formType, questions: clean };
      if (isEdit) await api.put(`/forms/${template.id}`, { ...payload, status: template.status || 'active' });
      else await api.post('/forms', payload);
      dirtyRef.current = false;
      toast.success(isEdit ? 'Template updated' : 'Template created');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={requestClose} dismissable={false} size="lg"
      title={isEdit ? 'Edit template' : 'New form template'}
      description="Staff answer these questions for each client, in this order."
      footer={(
        <>
          {tried && problemCount > 0 && (
            <span className="mr-auto text-[12.5px] font-medium text-rose-600" role="status">
              {problemCount === 1 ? 'Fix the problem highlighted above.' : `Fix the ${problemCount} problems highlighted above.`}
            </span>
          )}
          <button type="button" onClick={requestClose} disabled={saving} className="btn-secondary">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="btn-primary">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit ? 'Save changes' : 'Create template'}
          </button>
        </>
      )}>
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_13rem]">
          <Field label="Form name" required error={tried ? nameError : null}>
            {(id) => (
              <input id={id} className="input-field" value={name} maxLength={200} placeholder="For example, home safety checklist"
                aria-invalid={!!(tried && nameError)} onChange={(e) => { touch(); setName(e.target.value); }} />
            )}
          </Field>
          <Field label="Type">
            {(id) => (
              <select id={id} className="input-field" value={formType} onChange={(e) => { touch(); setFormType(e.target.value); }}>
                {(FORM_TYPES.includes(formType) ? FORM_TYPES : [...FORM_TYPES, formType]).map((t) => (
                  <option key={t} value={t}>{humanise(t)}</option>
                ))}
              </select>
            )}
          </Field>
        </div>

        <div>
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <h3 className="text-[13px] font-medium text-slate-700">Questions</h3>
            <span className="text-[12.5px] tabular-nums text-slate-500">{plural(questions.length, 'question')}</span>
          </div>
          <ol className="space-y-2">
            {questions.map((q, i) => {
              const isHeading = q.type === 'heading';
              const problem = tried ? problems[i] : null;
              return (
                <li key={q._k} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <div className="flex items-start gap-3">
                    <span className="mt-2.5 w-5 shrink-0 text-right text-[12px] font-semibold tabular-nums text-slate-500" aria-hidden="true">{i + 1}</span>
                    <div className="min-w-0 flex-1 space-y-2">
                      <input id={`qb-${q._k}`} className="input-field bg-white" value={q.label || ''}
                        aria-label={isHeading ? `Section ${i + 1} title` : `Question ${i + 1}`}
                        placeholder={isHeading ? 'Section title' : 'Question text'} aria-invalid={!!problem}
                        onChange={(e) => setQ(i, { label: e.target.value })} />
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <select className="input-field !w-auto bg-white text-sm" aria-label={`Answer type for question ${i + 1}`}
                          value={q.type || 'text'}
                          onChange={(e) => setQ(i, e.target.value === 'heading' ? { type: 'heading', required: false } : { type: e.target.value })}>
                          {typeOptionsFor(q.type).map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || humanise(t)}</option>)}
                        </select>
                        {!isHeading && (
                          <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-slate-700">
                            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 accent-primary-600" checked={!!q.required}
                              onChange={(e) => setQ(i, { required: e.target.checked })} />
                            Required
                          </label>
                        )}
                      </div>
                      {HAS_CHOICES.has(q.type) && (
                        <div>
                          <label htmlFor={`qc-${q._k}`} className="mb-1 block text-[12.5px] font-medium text-slate-700">Choices, one per line</label>
                          <textarea id={`qc-${q._k}`} rows={3} className="input-field bg-white text-sm" value={q._choices || ''}
                            onChange={(e) => setQ(i, { _choices: e.target.value })} />
                        </div>
                      )}
                      {problem && <p className="text-[12px] text-rose-600" role="alert">{problem}</p>}
                    </div>
                    <div className="flex shrink-0 flex-col gap-1">
                      <button type="button" className={ICON_BTN} aria-label={`Move question ${i + 1} up`} disabled={i === 0}
                        onClick={() => moveQ(i, -1)}><ChevronUp size={16} /></button>
                      <button type="button" className={ICON_BTN} aria-label={`Move question ${i + 1} down`} disabled={i === questions.length - 1}
                        onClick={() => moveQ(i, 1)}><ChevronDown size={16} /></button>
                      <button type="button" className={ICON_BTN_DANGER} aria-label={`Remove question ${i + 1}`} disabled={questions.length === 1}
                        onClick={() => delQ(i)}><Trash2 size={15} /></button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
          <button type="button" onClick={addQ} className="btn-secondary btn-sm !h-9 mt-3"><Plus size={14} /> Add question</button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Template preview ─────────────────────────────────────────────────────────

function TemplatePreview({ template, canEdit, hasUsage, onClose, onEdit, onDuplicate }) {
  const sections = sectionsOf(questionsOf(template));
  return (
    <DetailDrawer open onClose={onClose} width="md" title={template.name} subtitle={categoryLabel(template)}
      meta={[
        { label: 'Fields', value: num(fieldCount(template)) },
        hasUsage && { label: 'Last used', value: template.last_used_epoch ? fmtInstant(template.last_used_epoch * 1000) : 'Not used yet' },
        hasUsage && { label: 'Submissions', value: num(template.submission_count || 0) },
        { label: 'Source', value: template.is_template === false ? 'Built by your organisation' : 'Shared NDIS template' },
      ]}
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={() => onDuplicate(template)}><Copy size={14} /> Duplicate</button>
          {canEdit && <button type="button" className="btn-primary" onClick={() => onEdit(template)}><Pencil size={14} /> Edit template</button>}
        </>
      )}>
      {!canEdit && (
        <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-[13px] leading-relaxed text-slate-600">
          This is a shared template, so it can&apos;t be changed here. Duplicate it to make a version your organisation can edit.
        </p>
      )}
      {template.description && <p className="mb-4 text-[13.5px] leading-relaxed text-slate-600">{template.description}</p>}
      {sections.length === 0 ? (
        <EmptyState icon={FileText} title="This template has no questions yet" hint={canEdit ? 'Edit the template to add its questions.' : undefined} />
      ) : sections.map((s, si) => (
        <section key={si} className={si ? 'pt-5' : ''}>
          {s.heading && <h4 className="border-b border-slate-200 pb-2 text-[13px] font-semibold text-slate-900">{s.heading}</h4>}
          {s.fields.length > 0 ? (
            <ol className="divide-y divide-slate-100">
              {s.fields.map((q, qi) => (
                <li key={`${qKey(q)}-${qi}`} className="py-2.5">
                  <p className="text-[13.5px] text-slate-900">
                    {q.label}
                    {q.required && <span className="ml-1.5 text-[12px] font-medium text-slate-500">Required</span>}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-slate-500">{typeDescription(q)}</p>
                </li>
              ))}
            </ol>
          ) : (
            <p className="py-2.5 text-[12.5px] text-slate-500">No questions in this section</p>
          )}
        </section>
      ))}
    </DetailDrawer>
  );
}

// ─── Panels ───────────────────────────────────────────────────────────────────

function TemplatesPanel({ templates, loading, hasUsage, canEdit, onNew, onPreview, onEdit, onDuplicate, onDelete }) {
  const newButton = (
    <button type="button" className="btn-primary btn-sm !h-9" onClick={onNew}><Plus size={14} /> New template</button>
  );
  const columns = [
    {
      key: 'name', header: 'Name', sortable: true, sortValue: (t) => String(t.name || '').toLowerCase(),
      render: (t) => (
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <button type="button" onClick={(e) => { e.stopPropagation(); onPreview(t); }}
            className="rounded text-left font-semibold text-slate-900 underline-offset-2 hover:text-primary-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
            {t.name}
          </button>
          {t.is_template === false && <span className="badge badge-gray">Custom</span>}
        </span>
      ),
    },
    {
      key: 'category', header: 'Category', width: 190, sortable: true, sortValue: (t) => categoryLabel(t),
      render: (t) => <span className="text-slate-500">{categoryLabel(t)}</span>,
    },
    {
      key: 'fields', header: 'Fields', width: 90, align: 'right', sortable: true, sortValue: (t) => fieldCount(t), defaultDir: 'desc',
      render: (t) => <span className="tabular-nums">{fieldCount(t)}</span>,
    },
    hasUsage && {
      key: 'last_used', header: 'Last used', width: 140, sortable: true, defaultDir: 'desc',
      sortValue: (t) => (t.last_used_epoch ? Number(t.last_used_epoch) : null),
      render: (t) => (t.last_used_epoch
        ? <span className="tabular-nums">{fmtInstant(t.last_used_epoch * 1000)}</span>
        : <span className="text-slate-500">Not used yet</span>),
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, width: 60, align: 'right',
      render: (t) => (
        <OverflowMenu label={`Actions for ${t.name}`} size={16}
          className="!p-0 inline-flex h-9 w-9 items-center justify-center"
          items={[
            { label: 'Preview', icon: Eye, onClick: () => onPreview(t) },
            canEdit(t) && { label: 'Edit', icon: Pencil, onClick: () => onEdit(t) },
            { label: 'Duplicate', icon: Copy, onClick: () => onDuplicate(t) },
            canEdit(t) && { divider: true },
            canEdit(t) && { label: 'Delete', icon: Trash2, danger: true, onClick: () => onDelete(t) },
          ]} />
      ),
    },
  ];
  return (
    <>
      <Toolbar title={loading ? 'Form templates' : plural(templates.length, 'form template')} actions={newButton} />
      <DataTable columns={columns} rows={templates} rowKey="id" loading={loading} label="Form templates"
        defaultSort={{ key: 'name', dir: 'asc' }} onRowClick={onPreview} stickyHeader={false}
        empty={{
          icon: FileText,
          title: 'No form templates yet',
          hint: 'Build the forms your team fills in for each client, such as a risk assessment or a consent form.',
          action: newButton,
        }} />
    </>
  );
}

function ClientFormRow({ template, sub, onFill, onView, onPdf }) {
  const done = !!sub && sub.status === 'completed';
  const review = reviewOf(sub);
  const when = instantOf(sub);
  return (
    <li className="flex flex-col gap-x-4 gap-y-2.5 px-5 py-3.5 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[13.5px] font-semibold text-slate-900">{template.name}</span>
          <StatusPill status={!sub ? 'not_started' : done ? 'completed' : 'draft'} />
        </div>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12.5px] text-slate-500">
          <span>{categoryLabel(template)}</span>
          {when != null && (
            <>
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">{done ? 'Completed' : 'Started'} {fmtInstant(when)}</span>
            </>
          )}
          {review && (
            <>
              <span aria-hidden="true">·</span>
              {review.overdue ? (
                <span className="inline-flex items-center gap-1 font-medium text-rose-700">
                  <AlertTriangle size={12} aria-hidden="true" className="shrink-0" />
                  Review overdue (was due {fmtDate(review.date)})
                </span>
              ) : (
                <span>Review due {fmtDate(review.date)}</span>
              )}
            </>
          )}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!sub && (
          <button type="button" className="btn-secondary btn-sm !h-9" onClick={() => onFill(template, null)}>
            <Plus size={14} /> Start<span className="sr-only"> {template.name}</span>
          </button>
        )}
        {sub && !done && (
          <button type="button" className="btn-secondary btn-sm !h-9" onClick={() => onFill(template, sub)}>
            <Pencil size={14} /> Continue<span className="sr-only"> {template.name}</span>
          </button>
        )}
        {done && (
          <button type="button" className="btn-secondary btn-sm !h-9" onClick={() => onView(template, sub)}>
            <Eye size={14} /> View<span className="sr-only"> {template.name}</span>
          </button>
        )}
        {sub && (
          <button type="button" className="btn-ghost !p-0 h-9 w-9" onClick={() => onPdf(template, sub)}
            aria-label={`Download PDF of ${template.name}`} title="Download PDF">
            <Download size={16} />
          </button>
        )}
      </div>
    </li>
  );
}

function ClientFormsPanel({ client, templates, subs, loadState, onRetry, onFill, onView, onPdf, onDownloadCompleted }) {
  const stats = subs ? completionOf(subs, templates) : null;
  const pct = stats && stats.total ? (stats.completed / stats.total) * 100 : 0;
  return (
    <div>
      <div className="border-b border-slate-100 px-5 pb-4 pt-5">
        <PageHeader
          title={(
            <span className="flex min-w-0 items-center gap-3">
              <span aria-hidden="true" className="shrink-0">
                <PersonAvatar id={client.id} firstName={client.first_name} lastName={client.last_name} photo={client.profile_photo_url} size="md" />
              </span>
              <span className="min-w-0 break-words">{clientName(client)}</span>
            </span>
          )}
          description={stats ? (
            <span className="tabular-nums">
              {stats.completed} of {stats.total} forms complete · {stats.draft} in draft · {stats.notStarted} not started
            </span>
          ) : 'Loading forms…'}
          after={stats && stats.total > 0 ? (
            <div className="mt-3 max-w-md space-y-2">
              <ProgressBar pct={pct} tone="emerald" className="!h-1.5" />
              {stats.overdue > 0 && (
                <p className="flex items-center gap-1.5 text-[12.5px] font-medium text-rose-700">
                  <AlertTriangle size={13} aria-hidden="true" className="shrink-0" />
                  {plural(stats.overdue, 'review')} overdue
                </p>
              )}
            </div>
          ) : null}
          actions={(
            <button type="button" className="btn-secondary btn-sm !h-9" onClick={onDownloadCompleted} disabled={!stats || stats.completed === 0}>
              <Download size={14} /> Download completed PDFs
            </button>
          )}
        />
      </div>
      {loadState === 'error' ? (
        <ErrorState message="We couldn't load this client's forms. Check your connection, then try again." onRetry={onRetry} />
      ) : !subs ? (
        <TableSkeleton rows={6} cols={3} />
      ) : templates.length === 0 ? (
        <EmptyState icon={FileText} title="No form templates yet" hint="Create a template under Form templates; it then appears here for every client." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {templates.map((t) => (
            <ClientFormRow key={t.id} template={t} sub={subs[t.id]} onFill={onFill} onView={onView} onPdf={onPdf} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ClientRow({ client, stats, active, onOpen }) {
  const pct = stats.total ? (stats.completed / stats.total) * 100 : 0;
  return (
    <button type="button" onClick={() => onOpen(client)} aria-current={active ? 'true' : undefined}
      className={clsx(ROW_BTN, active ? 'bg-primary-50' : 'hover:bg-slate-50')}>
      <PersonAvatar id={client.id} firstName={client.first_name} lastName={client.last_name} photo={client.profile_photo_url} size="md" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-medium text-slate-900">{clientName(client)}</span>
        {stats.total > 0 ? (
          <>
            <span className="mt-1.5 flex items-center gap-2">
              <span aria-hidden="true" className="block h-1 w-20 shrink-0 overflow-hidden rounded-full bg-slate-100">
                <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
              </span>
              <span className="whitespace-nowrap text-[12px] tabular-nums text-slate-500">{stats.completed} of {stats.total} complete</span>
            </span>
            {stats.overdue > 0 && (
              <span className="mt-1 flex items-center gap-1 text-[12px] font-medium text-rose-700">
                <AlertTriangle size={12} aria-hidden="true" className="shrink-0" />
                {plural(stats.overdue, 'review')} overdue
              </span>
            )}
          </>
        ) : (
          <span className="mt-0.5 block text-[12px] text-slate-500">No forms to complete</span>
        )}
      </span>
    </button>
  );
}

function ClientListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading clients">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
          <div className="skeleton h-10 w-10 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-3.5 w-2/3" />
            <div className="skeleton h-2 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Forms() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const companyName = (user && user.orgName) || 'Rostery';
  const [open, setOpen] = useUrlTab('open', '');
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | error | ready
  const [clientSearch, setClientSearch] = useState('');
  const [clientData, setClientData] = useState({ id: null, state: 'idle', rows: [] });
  const [fill, setFill] = useState(null);       // { template, submission, client }
  const [viewing, setViewing] = useState(null); // { template, submission, client }
  const [builder, setBuilder] = useState(null); // null | {} (new) | template (edit)
  const [previewId, setPreviewId] = useState(null);

  const openClientId = open && open !== 'templates' ? open : null;

  const loadTemplates = useCallback(async () => {
    const r = await api.get('/forms/templates');
    setTemplates(Array.isArray(r.data) ? r.data : []);
  }, []);

  const loadClients = useCallback(async () => {
    const r = await api.get('/clients?is_active=true&limit=500');
    setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || []));
  }, []);

  // One row per client per form. An older server has no summary, so fall back to the recent
  // submissions list (the newest 200) and reduce it the same way.
  const loadSummary = useCallback(async () => {
    let rows;
    try {
      rows = (await api.get('/forms/submissions/summary')).data;
    } catch {
      const all = (await api.get('/forms/submissions/all')).data || [];
      rows = Object.values(bestByClientForm(all)).flatMap((byForm) => Object.values(byForm));
    }
    setSummary(Array.isArray(rows) ? rows : []);
  }, []);

  const loadAll = useCallback(async () => {
    setStatus('loading');
    try {
      await Promise.all([loadTemplates(), loadClients(), loadSummary()]);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [loadTemplates, loadClients, loadSummary]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // The open client's own submissions, with their answers (for View, Continue and PDFs).
  const loadClientRows = useCallback(async (id, { silent = false } = {}) => {
    if (!id) return;
    if (!silent) setClientData({ id, state: 'loading', rows: [] });
    try {
      const r = await api.get('/forms/submissions/all', { params: { client_id: id } });
      setClientData((prev) => (prev.id === id ? { id, state: 'ready', rows: Array.isArray(r.data) ? r.data : [] } : prev));
    } catch {
      if (!silent) setClientData((prev) => (prev.id === id ? { id, state: 'error', rows: [] } : prev));
    }
  }, []);

  useEffect(() => { if (openClientId) loadClientRows(openClientId); }, [openClientId, loadClientRows]);

  // After a change the lists update quietly in place.
  const refresh = useCallback(() => {
    loadTemplates().catch(() => {});
    loadSummary().catch(() => {});
    if (openClientId) loadClientRows(openClientId, { silent: true });
  }, [loadTemplates, loadSummary, loadClientRows, openClientId]);

  const summaryMap = useMemo(() => bestByClientForm(summary), [summary]);
  const sortedTemplates = useMemo(
    () => [...templates].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'en-AU', { sensitivity: 'base' })),
    [templates],
  );
  const clientStats = useMemo(() => {
    const out = {};
    for (const c of clients) out[c.id] = completionOf(summaryMap[c.id], templates);
    return out;
  }, [clients, summaryMap, templates]);
  const filteredClients = useMemo(() => {
    const s = clientSearch.trim().toLowerCase();
    return s ? clients.filter((c) => clientName(c).toLowerCase().includes(s)) : clients;
  }, [clients, clientSearch]);
  const selectedClient = useMemo(() => clients.find((c) => c.id === openClientId) || null, [clients, openClientId]);
  const clientLoadState = openClientId ? (clientData.id === openClientId ? clientData.state : 'loading') : 'idle';
  const clientSubs = useMemo(
    () => (clientData.id === openClientId && clientData.state === 'ready' ? (bestByClientForm(clientData.rows)[openClientId] || {}) : null),
    [clientData, openClientId],
  );
  const hasUsage = templates.some((t) => Object.prototype.hasOwnProperty.call(t, 'last_used_epoch'));
  const previewTemplate = previewId ? templates.find((t) => t.id === previewId) || null : null;
  // The database refuses an edit to a form another organisation owns (the shared templates, for
  // most organisations). An older server does not say, so Edit and Delete stay offered.
  const canEdit = (t) => t.can_edit !== false;

  const closeDetail = useCallback(() => setOpen(''), [setOpen]);
  const openClient = useCallback((c) => setOpen(c.id), [setOpen]);
  const closePreview = useCallback(() => setPreviewId(null), []);
  const closeBuilder = useCallback(() => setBuilder(null), []);
  const closeFill = useCallback(() => setFill(null), []);
  const closeView = useCallback(() => setViewing(null), []);
  const previewTemplateRow = useCallback((t) => setPreviewId(t.id), []);

  const editTemplate = (t) => { setPreviewId(null); setBuilder(t); };

  const deleteTemplate = async (t) => {
    const ok = await confirm(`"${t.name}" will no longer be offered for new forms. Submissions already made against it are kept.`, {
      title: 'Delete this template?', confirmLabel: 'Delete template', danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/forms/${t.id}`);
      toast.success('Template deleted');
      setPreviewId((id) => (id === t.id ? null : id));
      refresh();
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Only an admin can delete a template.'
        : (e.response?.data?.message || 'Could not delete the template'));
    }
  };

  // A copy is this organisation's own form, so it can be edited even when the original is shared.
  const duplicateTemplate = async (t) => {
    try {
      const r = await api.post('/forms', {
        name: `${String(t.name || 'Untitled form').slice(0, 193)} (copy)`,
        formType: t.form_type || 'general',
        questions: questionsOf(t),
      });
      toast.success('Copy created. Rename it and make your changes.');
      setPreviewId(null);
      refresh();
      if (r.data && r.data.id) setBuilder(r.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not duplicate the template');
    }
  };

  const handleFill = (template, submission) => setFill({ template, submission, client: selectedClient });
  const handleView = (template, submission) => setViewing({ template, submission, client: selectedClient });
  const handlePdf = (template, submission) => printForms([{ submission, form: template }], clientName(selectedClient), companyName);
  const downloadCompleted = () => {
    if (!selectedClient || !clientSubs) return;
    const items = sortedTemplates
      .filter((t) => clientSubs[t.id] && clientSubs[t.id].status === 'completed')
      .map((t) => ({ submission: clientSubs[t.id], form: t }));
    if (!items.length) { toast.error('No completed forms to download yet'); return; }
    printForms(items, clientName(selectedClient), companyName);
  };

  if (status === 'error') {
    return (
      <div className={PAGE_WIDE}>
        <div className="rounded-xl border border-slate-200 bg-white">
          <ErrorState message="We couldn't load forms. Check your connection, then try again." onRetry={loadAll} />
        </div>
      </div>
    );
  }

  const loading = status === 'loading';
  const countLine = loading ? 'Loading clients…'
    : clientSearch.trim() ? `${num(filteredClients.length)} of ${plural(clients.length, 'active client')}`
      : plural(clients.length, 'active client');

  const templatesView = (
    <TemplatesPanel templates={templates} loading={loading} hasUsage={hasUsage} canEdit={canEdit}
      onNew={() => setBuilder({})} onPreview={previewTemplateRow} onEdit={editTemplate}
      onDuplicate={duplicateTemplate} onDelete={deleteTemplate} />
  );

  let clientView = null;
  if (openClientId) {
    if (selectedClient) {
      clientView = (
        <ClientFormsPanel client={selectedClient} templates={sortedTemplates} subs={clientSubs} loadState={clientLoadState}
          onRetry={() => loadClientRows(openClientId)} onFill={handleFill} onView={handleView} onPdf={handlePdf}
          onDownloadCompleted={downloadCompleted} />
      );
    } else if (loading) {
      clientView = <TableSkeleton rows={6} cols={3} />;
    } else {
      clientView = (
        <EmptyState icon={Users} title="This client isn't in the active list"
          hint="They may have been archived. Choose a client from the list."
          action={<button type="button" className="btn-secondary btn-sm !h-9" onClick={closeDetail}>Back to form templates</button>} />
      );
    }
  }

  return (
    <div className={PAGE_WIDE}>
      <Workbench detailOpen={!!open} onCloseDetail={closeDetail} offset="7rem">
        <WorkbenchList width="sm" label="Clients">
          <div className="sticky top-0 z-10 space-y-2 border-b border-slate-200 bg-white px-4 py-3">
            <SearchInput value={clientSearch} onChange={setClientSearch} placeholder="Search clients" />
            <p className="text-[12px] tabular-nums text-slate-500" aria-live="polite">{countLine}</p>
          </div>

          <button type="button" onClick={() => setOpen('templates')} aria-current={open === 'templates' ? 'true' : undefined}
            className={clsx(ROW_BTN, open === 'templates' ? 'bg-primary-50'
              : !open ? 'hover:bg-slate-50 lg:bg-primary-50 lg:hover:bg-primary-50' : 'hover:bg-slate-50')}>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
              <FileText size={18} aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13.5px] font-medium text-slate-900">Form templates</span>
              <span className="block text-[12.5px] tabular-nums text-slate-500">
                {loading ? 'Loading…' : plural(templates.length, 'template')}
              </span>
            </span>
          </button>

          {loading ? <ClientListSkeleton />
            : clients.length === 0 ? (
              <EmptyState icon={Users} title="No active clients"
                hint="Forms are filled in for a client. Add a client, then start their forms here."
                action={<Link to="/clients/new" className="btn-secondary btn-sm !h-9">Add client</Link>} />
            ) : filteredClients.length === 0 ? (
              <EmptyState icon={Users} title="No clients match that name"
                action={<button type="button" className="btn-secondary btn-sm !h-9" onClick={() => setClientSearch('')}>Clear search</button>} />
            ) : (
              <div>
                {filteredClients.map((c) => (
                  <ClientRow key={c.id} client={c} stats={clientStats[c.id] || completionOf(null, templates)}
                    active={c.id === openClientId} onOpen={openClient} />
                ))}
              </div>
            )}
        </WorkbenchList>

        <WorkbenchDetail label={selectedClient ? `Forms for ${clientName(selectedClient)}` : 'Form templates'}
          backLabel="Back to clients" placeholder={templatesView}>
          {open === 'templates' ? templatesView : clientView}
        </WorkbenchDetail>
      </Workbench>

      {previewTemplate && (
        <TemplatePreview template={previewTemplate} canEdit={canEdit(previewTemplate)} hasUsage={hasUsage}
          onClose={closePreview} onEdit={editTemplate} onDuplicate={duplicateTemplate} />
      )}

      {builder && (
        <TemplateBuilderDialog template={builder.id ? builder : null} onClose={closeBuilder} onSaved={refresh} />
      )}

      {fill && fill.client && (
        <FormFillDialog template={fill.template} client={fill.client} submission={fill.submission}
          onClose={closeFill} onSaved={refresh} />
      )}

      {viewing && viewing.client && (
        <ViewSubmissionDialog submission={viewing.submission} template={viewing.template} client={viewing.client}
          companyName={companyName} onClose={closeView} onDeleted={refresh} />
      )}
    </div>
  );
}
